import { useState, useEffect } from "react";
import { Receipt, Eye, FileText, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import API from "@/lib/api";
import ReceiptPreviewDialog from "@/components/ReceiptPreviewDialog";

/**
 * Patient receipt history card. Lists all paid payment_transactions for the
 * current user. Clicking any row opens an in-browser preview modal with
 * Print / Email / Share / Download actions — receipts are regenerated on
 * demand from the same `build_receipt_pdf` used at charge time.
 */
export default function ReceiptsCard() {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setItems(null);
    try {
      const { data } = await API.get("/receipts/mine");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      toast.error("Couldn't load your receipts. Please try again.");
    }
  };

  useEffect(() => { load(); }, []);

  const fmtMoney = (amt, ccy) => {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: (ccy || "USD") }).format(amt);
    } catch {
      return `$${Number(amt || 0).toFixed(2)} ${ccy || ""}`.trim();
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
      return iso;
    }
  };

  return (
    <section
      className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8"
      data-testid="receipts-card"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-purple-100 flex items-center justify-center">
          <Receipt className="w-5 h-5 text-purple-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Receipts</h2>
          <p className="text-xs text-slate-500">Every successful charge generates a downloadable PDF — handy for taxes, HSA/FSA, and your records.</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="text-xs font-medium text-slate-500 hover:text-purple-700 inline-flex items-center gap-1 px-3 py-1.5 rounded-full hover:bg-purple-50 transition-colors"
          data-testid="receipts-refresh"
          title="Refresh"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {items === null ? (
        <div className="grid grid-cols-1 gap-3" data-testid="receipts-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl h-16 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-slate-50 border border-slate-100 rounded-2xl px-6 py-10 text-center" data-testid="receipts-empty">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No receipts yet.</p>
          <p className="text-xs text-slate-400 mt-1">After your first successful charge, your receipt will appear here automatically.</p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="receipts-list">
          {items.map((r) => (
            <li
              key={r.session_id}
              className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
              data-testid={`receipt-row-${r.receipt_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900 truncate">{r.medication_name || "GLPFlo medication"}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-2 py-0.5">
                    {r.tier_label}
                  </span>
                  {r.is_renewal && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                      Renewal
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Receipt <span className="font-mono">{r.receipt_id}</span> · {fmtDate(r.paid_at)}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-base font-bold text-slate-900">{fmtMoney(r.amount, r.currency)}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPreview(r)}
                className="rounded-full text-xs shrink-0"
                data-testid={`receipt-download-${r.receipt_id}`}
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                View
              </Button>
            </li>
          ))}
        </ul>
      )}

      <ReceiptPreviewDialog
        open={!!preview}
        onClose={() => setPreview(null)}
        sessionId={preview?.session_id}
        receiptId={preview?.receipt_id}
        tierLabel={preview?.tier_label}
      />
    </section>
  );
}
